/**
 * LIFF Context
 * 參考 towconnect-web 的 AuthContext.tsx 模式
 * 管理 LIFF 初始化和 LINE 使用者狀態
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import liff from '@line/liff'
import type { Liff } from '@line/liff'
import type { LineUser } from '@/types'

// ========== Context 型別 ==========
interface LiffContextType {
  liff: Liff | null
  isLoggedIn: boolean
  lineUser: LineUser | null
  idToken: string | null  // LINE ID Token (JWT) - 用於後端驗證
  isLiffReady: boolean
  loading: boolean
  error: string | null
  login: (force?: boolean) => Promise<void>  // force=true 可強制重新授權（ID Token 過期時用）
  logout: () => void
  getIdToken: () => string | null  // 取得最新的 ID Token
}

// ========== 建立 Context ==========
const LiffContext = createContext<LiffContextType | undefined>(undefined)

// 強制重新授權的防迴圈旗標（sessionStorage）：
// 同一次進站只強制重登一次，避免「重登後仍取不到 Token」時無限跳轉。
const RELOGIN_ATTEMPT_KEY = 'towconnect-liff-relogin-attempted'

// ========== Custom Hook ==========
export const useLiff = (): LiffContextType => {
  const context = useContext(LiffContext)
  if (context === undefined) {
    throw new Error('useLiff must be used within a LiffProvider')
  }
  return context
}

// ========== Provider Props ==========
interface LiffProviderProps {
  children: ReactNode
}

// ========== Provider 實作 ==========
export const LiffProvider: React.FC<LiffProviderProps> = ({ children }) => {
  const [liffInstance, setLiffInstance] = useState<Liff | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [lineUser, setLineUser] = useState<LineUser | null>(null)
  const [idToken, setIdToken] = useState<string | null>(null)  // LINE ID Token
  const [isLiffReady, setIsLiffReady] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // ========== 初始化 LIFF ==========
  useEffect(() => {
    const initLiff = async () => {
      try {
        const liffId = import.meta.env.VITE_LIFF_ID

        if (!liffId) {
          throw new Error('LIFF_ID is not configured. Please set VITE_LIFF_ID in .env file')
        }

        // 初始化 LIFF
        await liff.init({ liffId })
        setLiffInstance(liff)
        setIsLiffReady(true)

        // 檢查登入狀態
        if (liff.isLoggedIn()) {
          setIsLoggedIn(true)

          // 清理 URL 中的授權碼參數（防止重複使用導致錯誤）
          const url = new URL(window.location.href)
          if (url.searchParams.has('code') || url.searchParams.has('state') || url.searchParams.has('liffClientId')) {
            // 移除 LIFF 相關參數
            url.searchParams.delete('code')
            url.searchParams.delete('state')
            url.searchParams.delete('liffClientId')
            url.searchParams.delete('liffRedirectUri')

            // 使用 replaceState 更新 URL（不會重新載入頁面）
            window.history.replaceState({}, document.title, url.pathname + url.search + url.hash)
          }

          // 取得使用者資料
          const profile = await liff.getProfile()
          const user: LineUser = {
            userId: profile.userId,
            displayName: profile.displayName,
            pictureUrl: profile.pictureUrl,
            statusMessage: profile.statusMessage,
          }
          setLineUser(user)

          // 儲存到 localStorage (可選)
          localStorage.setItem('towconnect-liff-user', JSON.stringify(user))

          // 取得 Access Token (用於呼叫 LINE API)
          const accessToken = liff.getAccessToken()
          if (accessToken) {
            localStorage.setItem('towconnect-liff-access-token', accessToken)
          }

          // 取得 ID Token (用於後端驗證用戶身份)
          // 這是 JWT 格式的 Token，後端需要向 LINE Verify API 驗證
          const lineIdToken = liff.getIDToken()
          if (lineIdToken) {
            setIdToken(lineIdToken)
            localStorage.setItem('towconnect-liff-id-token', lineIdToken)
            console.log('ID Token obtained successfully')
          } else {
            console.warn('ID Token not available - user may not have granted openid scope')
          }
        }
      } catch (err) {
        console.error('LIFF initialization failed:', err)
        const errorMessage = err instanceof Error ? err.message : 'LIFF 初始化失敗'

        // 如果是 "invalid authorization code" 錯誤，忽略並清理 URL
        if (errorMessage.includes('invalid authorization code') || errorMessage.includes('400')) {
          console.warn('Authorization code already used, cleaning URL and retrying...')

          // 清理 URL 參數
          const url = new URL(window.location.href)
          url.searchParams.delete('code')
          url.searchParams.delete('state')
          url.searchParams.delete('liffClientId')
          url.searchParams.delete('liffRedirectUri')

          // 重新載入頁面（不帶授權碼參數）
          window.location.href = url.pathname + url.search + url.hash
          return // 不設定 error，因為會重新載入
        }

        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    initLiff()
  }, [])

  // ========== 登入 ==========
  const login = async (force = false) => {
    if (!liffInstance) {
      throw new Error('LIFF is not initialized')
    }

    // force=true：即使 liff.isLoggedIn() 為 true 也重新授權。
    // 用於「LIFF session 還在、但 ID Token 已過期取不到」的情境——
    // 此時只靠 isLoggedIn() 判斷會永遠不重新登入，導致 API 一直 403。
    if (force || !liffInstance.isLoggedIn()) {
      // 使用當前頁面的 URL 作為 redirectUri，確保登入後回到當前環境
      const redirectUri = window.location.origin + window.location.pathname
      liffInstance.login({ redirectUri })
    }
  }

  // ========== 登出 ==========
  const logout = () => {
    if (!liffInstance) {
      throw new Error('LIFF is not initialized')
    }

    if (liffInstance.isLoggedIn()) {
      liffInstance.logout()
      setIsLoggedIn(false)
      setLineUser(null)
      setIdToken(null)
      localStorage.removeItem('towconnect-liff-user')
      localStorage.removeItem('towconnect-liff-access-token')
      localStorage.removeItem('towconnect-liff-id-token')
    }
  }

  // ========== JWT Token 過期檢查 ==========
  // 解碼 JWT Token 並檢查是否過期
  const isTokenExpired = (token: string): boolean => {
    try {
      // JWT 格式: header.payload.signature
      const parts = token.split('.')
      if (parts.length !== 3) {
        return true
      }

      // 解碼 payload (Base64URL)
      const payload = parts[1]
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      const claims = JSON.parse(decoded)

      // 檢查 exp claim
      if (!claims.exp) {
        return true
      }

      // exp 是 Unix timestamp (秒)
      const expirationTime = claims.exp * 1000
      const now = Date.now()

      // 計算剩餘時間
      const remainingMs = expirationTime - now
      const remainingMinutes = Math.floor(remainingMs / 60000)
      const remainingSeconds = Math.floor((remainingMs % 60000) / 1000)

      console.log('LINE ID Token expiration check:', {
        exp: new Date(expirationTime).toISOString(),
        now: new Date(now).toISOString(),
        remaining: `${remainingMinutes}m ${remainingSeconds}s`,
        iat: claims.iat ? new Date(claims.iat * 1000).toISOString() : 'N/A'
      })

      // 只有真正過期才返回 true（移除 buffer，讓後端做最終驗證）
      const isExpired = now >= expirationTime

      if (isExpired) {
        console.warn('LINE ID Token is EXPIRED', {
          expiredAgo: `${Math.abs(remainingMinutes)}m ${Math.abs(remainingSeconds)}s ago`
        })
      }

      return isExpired
    } catch (error) {
      console.error('Failed to decode JWT token:', error)
      return true // 無法解碼視為過期
    }
  }

  // ========== 取得最新的 ID Token ==========
  // ID Token 可能會過期，此方法返回 Token（不做客戶端過期檢查）
  // 讓後端做最終驗證，後端返回 401 時再觸發重新登入
  const getIdToken = (): string | null => {
    if (!liffInstance) {
      console.log('getIdToken: LIFF not ready')
      return null
    }

    const token = liffInstance.isLoggedIn() ? liffInstance.getIDToken() : null

    if (!token) {
      // 取不到 ID Token 有兩種情形：
      //   (a) LIFF session 過期（久未使用）—— isLoggedIn() 仍可能為 true，但 getIDToken() 回 null
      //   (b) 使用者未授權 openid scope
      // 兩者都必須「強制重新授權」才能取得新 Token，否則後續 API 全數 403。
      // ⚠️ 防無限跳轉：同一次進站（sessionStorage 生命週期）只強制重登一次；
      //    若重登後仍取不到（多半是 (b)），就停手讓畫面顯示錯誤，不再跳轉。
      if (!sessionStorage.getItem(RELOGIN_ATTEMPT_KEY)) {
        sessionStorage.setItem(RELOGIN_ATTEMPT_KEY, '1')
        console.warn('getIdToken: ID Token 取不到，強制重新授權 LIFF')
        liffInstance.login({ redirectUri: window.location.origin + window.location.pathname })
      } else {
        console.error('getIdToken: 重新授權後仍取不到 ID Token，請確認已授權 openid scope')
      }
      return null
    }

    // 取得成功 → 清除重登旗標，讓下次過期時能再次觸發
    sessionStorage.removeItem(RELOGIN_ATTEMPT_KEY)

    // 只記錄 Token 狀態，不阻止返回（讓後端做最終驗證）
    // 這避免了 LIFF SDK 快取導致的問題
    const expiredStatus = isTokenExpired(token)
    if (expiredStatus) {
      console.warn('getIdToken: Token appears expired on client, but sending to backend for final verification')
    }

    // Token 存在，更新狀態
    if (token !== idToken) {
      setIdToken(token)
      localStorage.setItem('towconnect-liff-id-token', token)
    }

    return token
  }

  // ========== Context Value ==========
  const value: LiffContextType = {
    liff: liffInstance,
    isLoggedIn,
    lineUser,
    idToken,
    isLiffReady,
    loading,
    error,
    login,
    logout,
    getIdToken,
  }

  return <LiffContext.Provider value={value}>{children}</LiffContext.Provider>
}
